import { useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system' | 'halloween';
const KEY = 'nexo.theme';
/** Year in which the user picked another theme during the Halloween season. */
const OPT_OUT_KEY = 'nexo.theme.halloweenOptOut';
const media = () => window.matchMedia('(prefers-color-scheme: dark)');

/** 20–31 October, local time. Keep in sync with the boot script in index.html. */
export function isHalloweenSeason(date = new Date()) {
  return date.getMonth() === 9 && date.getDate() >= 20;
}

/** During the season everyone gets Halloween unless they chose something else this year. */
function effective(stored: ThemePreference, optOutYear: string | null): ThemePreference {
  const year = String(new Date().getFullYear());
  return isHalloweenSeason() && optOutYear !== year ? 'halloween' : stored;
}

function apply(preference: ThemePreference) {
  // Halloween is a dark palette with its own overrides on top (`.dark.halloween`).
  const dark =
    preference === 'dark' ||
    preference === 'halloween' ||
    (preference === 'system' && media().matches);
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.classList.toggle('halloween', preference === 'halloween');
}

export function useTheme(): [ThemePreference, (value: ThemePreference) => void] {
  const [stored, setStored] = useState<ThemePreference>(
    () => (localStorage.getItem(KEY) as ThemePreference | null) ?? 'system',
  );
  const [optOutYear, setOptOutYear] = useState(() => localStorage.getItem(OPT_OUT_KEY));
  const preference = effective(stored, optOutYear);

  useEffect(() => {
    apply(preference);
    if (preference !== 'system') return;
    const query = media();
    const onChange = () => apply('system');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  const choose = (value: ThemePreference) => {
    localStorage.setItem(KEY, value);
    setStored(value);
    if (!isHalloweenSeason()) return;
    const year = value === 'halloween' ? null : String(new Date().getFullYear());
    if (year) localStorage.setItem(OPT_OUT_KEY, year);
    else localStorage.removeItem(OPT_OUT_KEY);
    setOptOutYear(year);
  };

  return [preference, choose];
}
